import { ChangeDetectorRef, Component, DestroyRef, effect, ElementRef, HostListener, inject, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
    MatTableDataSource,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
} from '@angular/material/table';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Observable, filter, forkJoin, switchMap } from 'rxjs';
import { Device } from 'src/app/models/centrex/device';
import { DeviceService } from 'src/app/services/centrex/device.service';
import { DialogDeleteConfirmationComponent } from '../../dialogs/dialog-delete-confirmation/dialog-delete-confirmation.component';
import { ExtensionService } from 'src/app/services/centrex/extension.service';
import { DialogDeviceNotifyConfirmationComponent } from '../../dialogs/dialog-device-notify-confirmation/dialog-device-notify-confirmation.component';
import { Extension } from 'src/app/models/centrex/extension';
import { DeviceModelDisplayPipe } from 'src/app/pipes/device-model-display.pipe';
import { ExtensionDisplayPipe } from 'src/app/pipes/extension-display.pipe';
import { RegistrationStates } from 'src/app/models/centrex/enums/registration-states';
import { SelectionContext } from 'src/app/models/selection-context';
import { IAllSelectable } from 'src/app/models/interfaces/all-selectable.interface';
import { TranslateDatePipe } from 'src/app/pipes/translate-date.pipe';
import { TranslateDatePipe as TranslateDatePipe_1 } from '../../../pipes/translate-date.pipe';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatOption } from '@angular/material/core';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { MatChip, MatChipSet } from '@angular/material/chips';
import { JsonPipe } from '@angular/common';
import { AsyncPipe } from '@angular/common';
import { InstanceService } from 'src/app/services/centrex/instance.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-devices',
    templateUrl: './devices.component.html',
    styleUrls: ['./devices.component.scss'],
    imports: [
        FaIconComponent,
        MatButton,
        MatIcon,
        RouterLink,
        ReactiveFormsModule,
        MatTable,
        MatSort,
        MatColumnDef,
        MatHeaderCellDef,
        MatHeaderCell,
        MatSortHeader,
        MatFormField,
        MatInput,
        MatIconButton,
        MatSuffix,
        MatTooltip,
        MatCellDef,
        MatCell,
        MatSelect,
        MatSelectTrigger,
        MatOption,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        MatHeaderRowDef,
        MatHeaderRow,
        MatRowDef,
        MatRow,
        MatPaginator,
        TranslateModule,
        TranslateDatePipe_1,
    ],
})
export class DevicesComponent implements IAllSelectable {
    private readonly instanceService = inject(InstanceService);
    private destroyRef = inject(DestroyRef);

    // Icône
    public faAngleRight = faAngleRight;

    displayedColumns: string[] = [];

    dataSource: MatTableDataSource<Device>;
    deleteConfirmationDialog: MatDialogRef<DialogDeleteConfirmationComponent>;
    rebootConfirmationDialog: MatDialogRef<DialogDeviceNotifyConfirmationComponent>;

    filtersForm: UntypedFormGroup;
    registrationStates = RegistrationStates;
    devicesStates = Object.keys(RegistrationStates);

    /**
     * Contexte de la selection du tableau des contacts.
     */
    public selectionContext: SelectionContext;

    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;

    constructor(
        private deviceService: DeviceService,
        private extensionService: ExtensionService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private toastr: ToastrService,
        private formbuilder: UntypedFormBuilder,
        private spinner: NgxSpinnerService,
        private deviceModelDisplayPipe: DeviceModelDisplayPipe,
        private extensionDisplayPipe: ExtensionDisplayPipe,
        private translateDatePipe: TranslateDatePipe,
        private devicesTableRef: ElementRef,
        private cdRef: ChangeDetectorRef
    ) {
        this.selectionContext = new SelectionContext(this.cdRef, this);
        this.filtersForm = new UntypedFormGroup({});

        this.displayedColumns = ['address', 'name', 'state', 'date', 'model', 'userState', 'actions'];

        this.initFiltersForm();

        effect(() => {
            if (this.instanceService.instance()) {
                this.loadDatas();
                this.initFiltersForm();
            }
        });
    }

    getAllIds(): number[] {
        return this.dataSource.data.map((device: Device, index: number) => index);
    }

    /**
     * Initialisation du formulaire des filtres du tableau des périphériques
     */
    initFiltersForm() {
        this.filtersForm = this.formbuilder.group({
            address: [''],
            name: [''],
            state: [''],
            date: [''],
            model: [''],
            userState: [''],
        });
    }

    loadDatas() {
        this.spinner.show();

        forkJoin([this.extensionService.getExtensions(), this.deviceService.getDevices()])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: ([extensions, devices]) => {
                    if (devices) {
                        devices?.forEach((device) => {
                            // // Créé une propriété contenant les extensions du périphérique
                            // device['extensions'] = extensions
                            //     .sort((extA: Extension, extB: Extension) => (extA.type > extB.type ? 1 : 0))
                            //     .filter((ext: Extension) => device.extensionIds.includes(ext.id));

                            // // Créé une propriété pour afficher la liste des extensions ([numéro] - [nom] ([si groupe])
                            // device['displayExtensions'] = (device['extensions'] as Extension[]).map((ext) => this.extensionDisplayPipe.transform(ext)).join(', ');

                            device['displayModel'] = this.deviceModelDisplayPipe.transform(device, false);

                            device['userState'] =
                                extensions
                                    .filter((ext: Extension) => device.extensionIds.includes(ext.id))
                                    .findIndex((x) => x.registrationState === RegistrationStates.registered) >= 0
                                    ? 'true'
                                    : 'false';
                        });

                        this.dataSource = new MatTableDataSource(devices);

                        this.dataSource.paginator = this.paginator;
                        this.dataSource.sort = this.sort;

                        // Pour que le tri des colonnes avec pipe ou créées lors du chargement fonctionnent
                        this.dataSource.sortingDataAccessor = (device: Device, sortHeaderId: string) => {
                            // if (sortHeaderId === 'extensions') {
                            //     return device['displayExtensions'];
                            // } else
                            if (sortHeaderId === 'model') {
                                return device['displayModel'];
                            } else {
                                return device[sortHeaderId];
                            }
                        };

                        this.createFilters();
                    }

                    this.spinner.hide();
                },
                error: () => {
                    this.spinner.hide();
                },
            });
    }

    createFilters() {
        this.dataSource.filterPredicate = (device: Device, filter: string) => {
            const filterArray = filter.split('¤');
            const matchFilter = [];

            matchFilter.push(device.address && device.address.trim().toLowerCase().includes(filterArray[0].trim().toLowerCase()));

            matchFilter.push(device.name && device.name.trim().toLowerCase().includes(filterArray[1].trim().toLowerCase()));

            const states = filterArray[2] ? filterArray[2].split(',') : null;

            if (states) {
                matchFilter.push(device.state && states.includes(device.state.trim().toLowerCase()));
            }

            if (device.state === RegistrationStates.registered) {
                matchFilter.push(
                    (device.registrationTime ? this.translateDatePipe.transform(new Date(device.registrationTime), 'short') : '').includes(filterArray[3].trim().toLowerCase())
                );
            } else {
                matchFilter.push(
                    (device.unregistrationTime ? this.translateDatePipe.transform(new Date(device.unregistrationTime), 'short') : '').includes(filterArray[3].trim().toLowerCase())
                );
            }

            matchFilter.push(
                device.manufacturer && device.modelName && this.deviceModelDisplayPipe.transform(device, false).toLowerCase().includes(filterArray[4].trim().toLowerCase())
            );

            const userStates = filterArray[5] ? filterArray[5].split(',') : null;

            if (userStates) {
                // matchFilter.push(device['displayExtensions'].trim().toLowerCase().includes(filterArray[5].trim().toLowerCase()));
                matchFilter.push(device['userState'] && userStates.includes(device['userState'].trim().toLowerCase()));
            }

            return matchFilter.every(Boolean);
        };

        this.applyFilters();
    }

    /**
     * Application du filtre dans le tableau des périphériques
     */
    applyFilters() {
        const address = this.filtersForm.get('address').value;
        const name = this.filtersForm.get('name').value;
        const state = this.filtersForm.get('state').value;
        const date = this.filtersForm.get('date').value;
        const model = this.filtersForm.get('model').value;
        const userState = this.filtersForm.get('userState').value;

        const filterValue =
            (!!address ? address : '') +
            '¤' +
            (!!name ? name : '') +
            '¤' +
            (!!state ? state : '') +
            '¤' +
            (!!date ? date : '') +
            '¤' +
            (!!model ? model : '') +
            '¤' +
            (!!userState ? userState : '');

        this.dataSource.filter = filterValue.trim().toLowerCase();

        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    /**
     * Réinitialise le filtre de la colonne spécifiée.
     * @param formControlName Nom du contrôle du formulaire
     */
    resetColumnFilter(formControlName: string): void {
        this.filtersForm.controls[formControlName].reset();
        this.applyFilters();
    }

    /**
     * Réinitialise tous les filtres de colonne
     */
    resetAllColumnFilter() {
        this.filtersForm.reset();
        this.applyFilters();
    }

    onDeleteClick(deviceAddress: string, deviceName: string) {
        this.deleteConfirmationDialog = this.dialog.open(DialogDeleteConfirmationComponent, {
            panelClass: 'default-dialog',
            disableClose: true,
        });
        this.deleteConfirmationDialog.componentInstance.confirmMessage = this.translate.instant('DialogDeleteConfirmation.DeviceConfirmMessage', {
            deviceName: deviceName,
        });

        this.deleteConfirmationDialog
            .afterClosed()
            .pipe(
                filter((result) => result === true),
                switchMap(() => this.deviceService.deleteDevice(deviceAddress)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.toastr.success(
                        this.translate.instant('Devices.SuccessMessageDeleteDevice', {
                            deviceName: deviceName,
                        })
                    );
                    this.loadDatas();
                },
                error: () => {
                    this.toastr.error(
                        this.translate.instant('Devices.ErrorMessageDeleteDevice', {
                            deviceName: deviceName,
                        })
                    );
                },
                complete: () => {
                    this.deleteConfirmationDialog = null;
                },
            });
    }

    onRebootClick(deviceAddress: string, deviceName: string) {
        let devicesToTreat: Device[] = this.getDevicesToTreat(this.selectionContext.selections);

        this.rebootConfirmationDialog = this.dialog.open(DialogDeviceNotifyConfirmationComponent, {
            panelClass: 'default-dialog',
            disableClose: true,
        });

        if (devicesToTreat.length > 1) {
            this.rebootConfirmationDialog.componentInstance.confirmMessage = this.translate.instant('DialogRebootsConfirmation.DeviceConfirmMessage', {
                devicesNames: this.getDevicesNames(devicesToTreat),
            });
            this.rebootConfirmationDialog.componentInstance.title = this.translate.instant('DialogRebootsConfirmation.Title');
        } else {
            this.rebootConfirmationDialog.componentInstance.confirmMessage = this.translate.instant('DialogRebootConfirmation.DeviceConfirmMessage', {
                deviceName: deviceName,
            });
            this.rebootConfirmationDialog.componentInstance.title = this.translate.instant('DialogRebootConfirmation.Title');
        }

        this.rebootConfirmationDialog
            .afterClosed()
            .pipe(
                filter((result) => result === true),
                switchMap(() => {
                    if (devicesToTreat.length > 1) {
                        let request = new Array<Observable<Device>>();

                        devicesToTreat.forEach((x) => {
                            request.push(this.deviceService.rebootDevice(x.address));
                        });
                        return forkJoin(request);
                    } else {
                        return this.deviceService.rebootDevice(deviceAddress);
                    }
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    if (devicesToTreat.length > 1) {
                        this.toastr.success(
                            this.translate.instant('Devices.SuccessMessageRebootDevices', {
                                devicesNames: this.getDevicesNames(devicesToTreat),
                            })
                        );
                    } else {
                        this.toastr.success(
                            this.translate.instant('Devices.SuccessMessageRebootDevice', {
                                deviceName: deviceName,
                            })
                        );
                    }
                    this.loadDatas();
                },
                error: () => {
                    if (devicesToTreat.length > 1) {
                        this.toastr.error(
                            this.translate.instant('Devices.ErrorMessageRebootDevices', {
                                devicesNames: this.getDevicesNames(devicesToTreat),
                            })
                        );
                    } else {
                        this.toastr.error(
                            this.translate.instant('Devices.ErrorMessageRebootDevice', {
                                deviceName: deviceName,
                            })
                        );
                    }
                },
                complete: () => {
                    this.rebootConfirmationDialog = null;
                },
            });
    }

    onRegisterClick(deviceAddress: string, deviceName: string) {
        let devicesToTreat: Device[] = this.getDevicesToTreat(this.selectionContext.selections);

        this.rebootConfirmationDialog = this.dialog.open(DialogDeviceNotifyConfirmationComponent, {
            panelClass: 'default-dialog',
            disableClose: true,
        });

        if (devicesToTreat.length > 1) {
            this.rebootConfirmationDialog.componentInstance.confirmMessage = this.translate.instant('DialogRegistersConfirmation.DeviceConfirmMessage', {
                devicesNames: this.getDevicesNames(devicesToTreat),
            });
            this.rebootConfirmationDialog.componentInstance.title = this.translate.instant('DialogRegistersConfirmation.Title');
        } else {
            this.rebootConfirmationDialog.componentInstance.confirmMessage = this.translate.instant('DialogRegisterConfirmation.DeviceConfirmMessage', {
                deviceName: deviceName,
            });
            this.rebootConfirmationDialog.componentInstance.title = this.translate.instant('DialogRegisterConfirmation.Title');
        }

        this.rebootConfirmationDialog
            .afterClosed()
            .pipe(
                filter((result) => result === true),
                switchMap(() => {
                    if (devicesToTreat.length > 1) {
                        let request = new Array<Observable<Device>>();

                        devicesToTreat.forEach((x) => {
                            request.push(this.deviceService.registerDevice(x.address));
                        });
                        return forkJoin(request);
                    } else {
                        return this.deviceService.registerDevice(deviceAddress);
                    }
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    if (devicesToTreat.length > 1) {
                        this.toastr.success(
                            this.translate.instant('Devices.SuccessMessageRegisterDevices', {
                                devicesNames: this.getDevicesNames(devicesToTreat),
                            })
                        );
                    } else {
                        this.toastr.success(
                            this.translate.instant('Devices.SuccessMessageRegisterDevice', {
                                deviceName: deviceName,
                            })
                        );
                    }
                    this.loadDatas();
                },
                error: () => {
                    if (devicesToTreat.length > 1) {
                        this.toastr.error(
                            this.translate.instant('Devices.ErrorMessageRegisterDevices', {
                                devicesNames: this.getDevicesNames(devicesToTreat),
                            })
                        );
                    } else {
                        this.toastr.error(
                            this.translate.instant('Devices.ErrorMessageRegisterDevice', {
                                deviceName: deviceName,
                            })
                        );
                    }
                },
                complete: () => {
                    this.rebootConfirmationDialog = null;
                },
            });
    }

    onProvisionningClick(deviceAddress: string, deviceName: string) {
        let devicesToTreat: Device[] = this.getDevicesToTreat(this.selectionContext.selections);

        this.rebootConfirmationDialog = this.dialog.open(DialogDeviceNotifyConfirmationComponent, {
            panelClass: 'default-dialog',
            disableClose: true,
        });

        if (devicesToTreat.length > 1) {
            this.rebootConfirmationDialog.componentInstance.confirmMessage = this.translate.instant('DialogProvisionningsConfirmation.DeviceConfirmMessage', {
                devicesNames: this.getDevicesNames(devicesToTreat),
            });
            this.rebootConfirmationDialog.componentInstance.title = this.translate.instant('DialogProvisionningsConfirmation.Title');
        } else {
            this.translate.instant('DialogProvisionningConfirmation.DeviceConfirmMessage', {
                deviceName: deviceName,
            });
            this.rebootConfirmationDialog.componentInstance.title = this.translate.instant('DialogProvisionningConfirmation.Title');
        }

        this.rebootConfirmationDialog
            .afterClosed()
            .pipe(
                filter((result) => result === true),
                switchMap(() => {
                    if (devicesToTreat.length > 1) {
                        let request = new Array<Observable<Device>>();

                        devicesToTreat.forEach((x) => {
                            request.push(this.deviceService.provisionningDevice(x.address));
                        });
                        return forkJoin(request);
                    } else {
                        return this.deviceService.provisionningDevice(deviceAddress);
                    }
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    if (devicesToTreat.length > 1) {
                        this.toastr.success(
                            this.translate.instant('Devices.SuccessMessageProvisionningDevices', {
                                devicesNames: this.getDevicesNames(devicesToTreat),
                            })
                        );
                    } else {
                        this.toastr.success(
                            this.translate.instant('Devices.SuccessMessageProvisionningDevice', {
                                deviceName: deviceName,
                            })
                        );
                    }
                    this.loadDatas();
                },
                error: () => {
                    if (devicesToTreat.length > 1) {
                        this.toastr.error(
                            this.translate.instant('Devices.ErrorMessageProvisionningDevices', {
                                devicesNames: this.getDevicesNames(devicesToTreat),
                            })
                        );
                    } else {
                        this.toastr.error(
                            this.translate.instant('Devices.ErrorMessageProvisionningDevice', {
                                deviceName: deviceName,
                            })
                        );
                    }
                },
                complete: () => {
                    this.rebootConfirmationDialog = null;
                },
            });
    }
    /**
     * Appelé lorsque l'utilisateur clique sur un élément de la liste.
     * @param event : Evenment clic de la souris.
     * @param index : Index de l'élément cliqué.
     */
    public onMouseClick(event: MouseEvent, index: number) {
        this.selectionContext.onMouseClick(event, index);
    }

    /**
     * Indique si l'élément specifié est selectionné.
     * @param index : index de l'élément à tester
     * @returns True si l'élément est selectionné, ou false autrement.
     */
    public isSelected(index: number): boolean {
        return this.selectionContext.isSelected(index);
    }

    /**
     * Appelé lorsque l'utilisateur appuie sur une touche du clavier.
     * @param event Événement du clavier.
     */
    @HostListener('document:keydown', ['$event'])
    private handleKeyboardEvent(event: KeyboardEvent) {
        if (event.cancelBubble === false) {
            this.selectionContext.handleKeyboardEvent(event);
        }
    }

    /**
     * Appelé lorsque l'utilisateur clique sur un élément quelconque de la page.
     * @param event Evenment clic de la souris.
     */
    @HostListener('document:click', ['$event'])
    private clickout(event) {
        if (this.selectionContext.selections.length && !this.devicesTableRef.nativeElement.contains(event.target)) {
            // Si la selection n'est pas vide et que l'utilisateur clique en dehors du containeur des éléments pouvant être glisser/déplacer, alors vide la selection.
            this.selectionContext.clearSelection();
        }
    }

    private getDevicesNames(devices: Device[]): string {
        let devicesNames: string = '';
        devices.forEach((x) => {
            devicesNames += `\"${x.name}\"\n`;
        });

        return devicesNames.substring(0, devicesNames.length - 1);
    }

    private getDevicesToTreat(indexDevices: number[]): Device[] {
        let devicesToTreat: Device[] = [];

        this.selectionContext.selections.forEach((x) => {
            if (this.dataSource.data[x].state === this.registrationStates.registered) {
                devicesToTreat.push(this.dataSource.data[x]);
            }
        });

        return devicesToTreat;
    }
}
